import ServiceLayout from "@/components/ServiceLayout";
import { QuoteModal } from "@/components/QuoteModal";
import { Button } from "@/components/ui/button";
import { Phone } from "lucide-react";

export default function Landsberg() {
  return (
    <ServiceLayout
      title="Umzugsunternehmen in Landsberg"
      description="Professionelle Umzüge und Möbelmontage in Landsberg am Lech. Wir machen Ihren Wohnungswechsel zur entspannten Angelegenheit."
      image="https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/Zrqvy6cNzSbnzDV55cv1/media/6936df934b202f5e27b43c42.jpg"
      heroBg="https://vibe.filesafe.space/1781514922662573702/assets/46649f47-2199-47d3-b0d3-06cd076035c0.png"
      content={
        <>
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight">
            Stressfrei umziehen in Landsberg
          </h2>
          <p className="text-lg text-neutral-600">
            Landsberg am Lech ist eine wunderschöne Stadt – und Ihr Umzug hierhin oder von hier weg sollte genauso schön und vor allem stressfrei verlaufen. Wir sind Ihr kompetenter Partner für alle Arten von Umzügen.
          </p>
          <p className="text-lg text-neutral-600">
            Egal ob kleine Wohnung oder großes Einfamilienhaus, wir passen unsere Leistungen exakt an Ihre Bedürfnisse an. Lehnen Sie sich zurück und überlassen Sie uns die schwere Arbeit.
          </p>
          <ul className="space-y-3 mt-6">
            {[
              "Individuelle Umzugsplanung",
              "Schonender Umgang mit Ihrem Eigentum",
              "Auf- und Abbau von Möbeln",
              "Besenreine Übergabe auf Wunsch"
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-3 text-neutral-800 font-medium">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </div>
                {item}
              </li>
            ))}
          </ul>
        </>
      }
      extendedContent={
        <div className="bg-white">
          <section className="py-16 bg-neutral-50">
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto space-y-8">
                <div>
                  <h2 className="font-heading text-3xl font-bold mb-4">Ihr verlässliches Umzugsunternehmen in Landsberg am Lech</h2>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Ein Umzug ist mehr als nur der Transport von Kisten und Möbeln – es ist der Start in einen neuen Lebensabschnitt. Damit dieser Start in Landsberg am Lech so reibungslos und stressfrei wie möglich verläuft, stehen wir Ihnen als professionelles Umzugsunternehmen zur Seite. Wir nehmen Ihnen die körperlich schwere und zeitaufwendige Arbeit ab, sodass Sie sich voll und ganz auf Ihr neues Zuhause konzentrieren können.
                  </p>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Landsberg am Lech besticht durch seine malerische Altstadt, enge Gassen und historische Gebäude. Diese Schönheit bringt bei einem Umzug oft logistische Herausforderungen mit sich. Unser erfahrenes Team kennt die örtlichen Gegebenheiten genau. Wir planen die Anfahrtswege, kümmern uns bei Bedarf um die Einrichtung von Halteverbotszonen und sorgen dafür, dass auch in engen Treppenhäusern alles sicher transportiert wird.
                  </p>
                  <div className="pt-4 pb-2 flex flex-col sm:flex-row items-start gap-4">
                    <QuoteModal>
                      <Button size="lg" className="font-bold text-base px-8 py-6 rounded-none bg-primary text-white hover:bg-primary/90 w-full sm:w-auto">
                        Kostenloses Angebot anfordern
                      </Button>
                    </QuoteModal>
                    <Button size="lg" variant="secondary" asChild className="w-full sm:w-auto">
                      <a href="tel:+4915888640762">
                        <Phone className="w-5 h-5 mr-2" />
                        +49 15888 640762
                      </a>
                    </Button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8 pt-8">
                  <div className="bg-white p-8 border border-neutral-200">
                    <h3 className="font-heading text-xl font-bold mb-3">Privatumzüge in Landsberg</h3>
                    <p className="text-neutral-600 leading-relaxed">
                      Vom Single-Haushalt bis zur Großfamilie – wir führen Privatumzüge in Landsberg zuverlässig und diskret durch. Unser Leistungsangebot reicht vom Standard-Transport bis hin zum bequemen Full-Service-Umzug, bei dem wir Ihnen sämtliche Arbeiten abnehmen.
                    </p>
                  </div>
                  <div className="bg-white p-8 border border-neutral-200">
                    <h3 className="font-heading text-xl font-bold mb-3">Büro- und Firmenumzüge</h3>
                    <p className="text-neutral-600 leading-relaxed">
                      Ein Standortwechsel Ihres Unternehmens erfordert präzise Planung, um den laufenden Betrieb so wenig wie möglich zu beeinträchtigen. Wir sorgen für einen schnellen und sicheren Transport Ihrer Büroeinrichtung und IT-Infrastruktur in Landsberg.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 bg-white">
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto space-y-8">
                <div>
                  <h2 className="font-heading text-3xl font-bold mb-4">Mehr Service für Ihren Umzug: Montage und Installation</h2>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Unser Ziel ist es, Ihnen einen Komplettservice zu bieten. Deshalb kümmern sich unsere geschulten Handwerker nicht nur um den Transport, sondern auch um die fachgerechte Demontage und Montage Ihrer Möbel. Auch komplizierte Schrankwände oder Einbauküchen werden von uns professionell abgebaut und in Ihrem neuen Zuhause in Landsberg wieder aufgebaut.
                  </p>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Darüber hinaus übernehmen wir Elektro- und Lichtinstallationen. Wir bringen Ihre Deckenlampen an, schließen den Elektroherd an und installieren Ihre Waschmaschine. Wenn Sie Ihre alte Wohnung renoviert übergeben müssen, stehen wir Ihnen auch für Bodenbelagsarbeiten und kleinere Schönheitsreparaturen zur Verfügung. Alles aus einer Hand – für maximale Entlastung.
                  </p>
                  <div className="pt-4 pb-2 flex flex-col sm:flex-row items-start gap-4">
                    <QuoteModal>
                      <Button size="lg" className="font-bold text-base px-8 py-6 rounded-none bg-primary text-white hover:bg-primary/90 w-full sm:w-auto">
                        Jetzt beraten lassen
                      </Button>
                    </QuoteModal>
                  </div>
                </div>

                <div className="bg-primary/5 p-8 border-l-4 border-primary mt-8">
                  <h3 className="font-heading text-2xl font-bold mb-3">Absolute Sicherheit und transparente Kosten</h3>
                  <p className="text-neutral-700 leading-relaxed">
                    Bei uns gibt es keine versteckten Kosten. Nach einer kostenlosen Besichtigung Ihres Umzugsguts in Landsberg erstellen wir Ihnen ein verbindliches Festpreisangebot. So können Sie Ihr Umzugsbudget genau kalkulieren. Und sollte wider Erwarten doch einmal etwas beschädigt werden, greift unsere umfassende Betriebshaftpflicht- und Transportversicherung.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 bg-neutral-950 text-white">
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="font-heading text-3xl font-bold mb-6">Warum Easy Umzug in Landsberg am Lech?</h2>
                <div className="grid sm:grid-cols-3 gap-8 mt-12 text-left">
                  <div>
                    <div className="w-12 h-12 bg-primary flex items-center justify-center mb-4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Kompetente Beratung</h3>
                    <p className="text-neutral-400">Wir beraten Sie im Vorfeld ausführlich und planen Ihren Umzug individuell nach Ihren Wünschen.</p>
                  </div>
                  <div>
                    <div className="w-12 h-12 bg-primary flex items-center justify-center mb-4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Sorgfalt & Vorsicht</h3>
                    <p className="text-neutral-400">Ihr Hab und Gut wird von uns professionell verpackt und im Transportfahrzeug sicher verstaut.</p>
                  </div>
                  <div>
                    <div className="w-12 h-12 bg-primary flex items-center justify-center mb-4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path></svg>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Faire Festpreise</h3>
                    <p className="text-neutral-400">Sie erhalten von uns ein transparentes Angebot ohne versteckte Kosten oder Nachzahlungen.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      }
    />
  );
}
